import React, { createContext, ReactNode, useContext, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { useSearchParams } from 'react-router-dom';
import { useAlertContext } from '../../../context/useAlertContext';
import { useAuthCodes } from '../../../hooks/useAuthCode';
import { useCheckApi } from '../../../hooks/useCheckApi';
import { useUserContext } from '../../../hooks/useUserContext';
import { Check, CheckItem } from '../../../models/Check.model';
import { Split } from '../../../models/Split.model';
import { SplitQuantity } from '../../../models/SplitQuantity.model';

interface CheckContextType {
  check: Check | null;
  claimCheckItemQuantity: (quantity: SplitQuantity) => Promise<void>;

  selectedItem: CheckItem | null;
  setSelectedItem: (item: CheckItem | null) => void;
  selectedSplit: Split | null;
  setSelectedSplit: (split: Split | null) => void;
}

const CheckContext = createContext<CheckContextType | undefined>(undefined);

export const CheckProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { checkId } = useParams<{ checkId?: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const { getAuthCode } = useAuthCodes();
  const navigate = useNavigate();

  const { setSuccess } = useAlertContext();
  const { user } = useUserContext();

  const { fetchCheck, claimCheckItems } = useCheckApi();

  const [check, setCheck] = useState<Check | null>(null);
  const [selectedItem, setSelectedItem] = React.useState<CheckItem | null>(null);
  const [selectedSplit, setSelectedSplit] = React.useState<Split | null>(null);

  const authCode = useMemo(() => {
    if (!checkId) return null;
    return getAuthCode(checkId);
  }, [checkId]);

  useEffect(() => {
    const getCheck = async () => {
      if (!checkId) return;

      const fetchedCheck = await fetchCheck(checkId, authCode || undefined);
      if (!fetchedCheck) {
        const shareCode = searchParams.get('share_code');
        navigate(`/join?share_code=${shareCode}`);
        return;
      }

      setCheck(fetchedCheck);
      setSearchParams(
        (params) => {
          params.set('share_code', fetchedCheck.shareCode);
          return params;
        },
        { replace: true }
      );
    };
    getCheck();
  }, [checkId, authCode, user]);

  const claimCheckItemQuantity = async (quantity: SplitQuantity) => {
    if (!checkId || !selectedItem) return;

    const item = {
      item_id: selectedItem.id,
      ...quantity.toApiInput(),
    };
    const claimedCheck = await claimCheckItems(checkId, [item], authCode ?? undefined);
    if (!claimedCheck) {
      const newCheck = await fetchCheck(checkId, authCode ?? undefined);
      if (!newCheck) return;

      setCheck(newCheck);
      setSelectedItem(newCheck.items.find((i) => i.id === selectedItem.id) || null);
      return;
    }

    setSuccess(`Claimed ${quantity.toString()} ${selectedItem.name}`);
    setCheck(claimedCheck);
    setSelectedItem(claimedCheck.items.find((i) => i.id === selectedItem.id) || null);
  };

  return (
    <CheckContext.Provider
      value={{
        check,
        claimCheckItemQuantity,
        selectedItem,
        setSelectedItem,
        selectedSplit,
        setSelectedSplit,
      }}
    >
      {children}
    </CheckContext.Provider>
  );
};

export const useCheckContext = () => {
  const context = useContext(CheckContext);
  if (context === undefined) {
    throw new Error('useCheckContext must be used within a CheckProvider');
  }
  return context;
};
